import { pointerWithin } from '@dnd-kit/core'

// Drag-and-drop rules for the ToC. Every row registers a draggable and a
// droppable carrying the same payload:
//   {id, depth, tag, childTag, pathIds, title}
// pathIds is the row's ancestor id chain including itself (the same ids that
// make up the row's reader URL); childTag is the tag of the row's first ToC
// child, or null when it has none.
//
// Items stay at their own level: a row is a valid sibling target for a
// same-depth, same-tag item, and a chapter header is a valid "drop inside"
// target for items one level deeper whose tag matches the chapter's existing
// children (an empty chapter takes sections, matching add_toc_item's
// convention). The server re-validates all of this in tools.move_toc_item.

export function isCompatibleDrop(active, over) {
  if (!active || !over || !over.id) return false
  if (over.id === active.id) return false
  if ((over.pathIds || []).includes(active.id)) return false // inside the dragged subtree
  if (over.depth === active.depth && over.tag === active.tag) return true
  if (over.depth === active.depth - 1 && over.tag === 'chapter') {
    return (
      over.childTag === active.tag ||
      (over.childTag == null && active.tag === 'section')
    )
  }
  return false
}

// Collision detection: only rows the dragged item may drop on, and only when
// the pointer is actually over one - hovering an invalid row gives no target.
export function tocCollisionDetection(args) {
  const activeData = args.active.data.current
  const compatible = args.droppableContainers.filter(container =>
    isCompatibleDrop(activeData, container.data.current)
  )
  return pointerWithin({ ...args, droppableContainers: compatible })
}

// Translates a drop into the move_toc_item request body, or null when the
// drop is invalid. Same-depth targets become before/after siblings depending
// on which half of the row the dragged item sits over; a parent-level chapter
// target appends inside it.
export function computeMoveRequest(active, over, activeRect, overRect) {
  if (!isCompatibleDrop(active, over)) return null
  if (over.depth === active.depth - 1) {
    return { node_id: active.id, target_id: over.id, position: 'inside' }
  }
  let position = 'before'
  if (activeRect && overRect) {
    const activeCenter = activeRect.top + activeRect.height / 2
    const overCenter = overRect.top + overRect.height / 2
    position = activeCenter > overCenter ? 'after' : 'before'
  }
  return { node_id: active.id, target_id: over.id, position }
}
